'use client';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useContext, } from 'react';
import { mergeProps, useButton, useFocusRing, useHover, useLink, useObjectRef, } from 'react-aria';
import { ButtonContext, useContextProps } from 'react-aria-components';
import { Icon } from '../system/icons';
import { ForceStateContext } from '../system/interactive';
import { MOTION, focusRing } from '../system/motion';
import { CONTROL_HEIGHT } from '../system/sizing';
import { TextTrim } from '../system/text-trim';
import { Spinner } from '../Spinner/Spinner';
import styles from './Button.module.css';
const TEXT_LINK_VARIANTS = new Set(['link', 'linkQuiet']);
const BTN_BORDER_WIDTH = '1px';
const BTN_BASE = {
    letterSpacing: '0.005em',
    borderRadius: 'var(--radius-full)',
    border: `${BTN_BORDER_WIDTH} solid transparent`,
    alignItems: 'center',
    gap: 'var(--space-sm)',
    cursor: 'pointer',
    transition: MOTION.transition,
    whiteSpace: 'nowrap',
};
// No vertical padding — centering is handled by the explicit `height` +
// `align-items: center` against the cap-trimmed label (see `TextTrim` in
// system/text-trim). Inline padding is BTN_PAD_X's.
//
// `sm` uses label-sm (12/600) so the small button sits at the same text
// tier as Pill size="sm" — the two primitives are routinely paired in
// list rows (e.g. GroupCard's "Joined" Pill next to the next card's
// "Join" Button) and need to visually align.
// `md` uses label (14/600) — the default control tier.
// `lg` keeps the md text tier at the hero control height — for prominent
// standalone actions (landing and journey CTAs, roomy modal footers), not
// toolbar rows that pair with md controls.
const BTN_SIZES = {
    sm: { font: 'var(--type-label-sm)', height: CONTROL_HEIGHT.sm },
    md: { font: 'var(--type-label)', height: CONTROL_HEIGHT.md },
    lg: { font: 'var(--type-label)', height: CONTROL_HEIGHT.lg },
};
const BTN_PAD_X = { sm: 'md', md: 'lg', lg: 'xl' };
// Icon-only mode: button collapses to a square hit-target sized off the
// height tier. Icons scale up so the glyph reads at the larger button size.
const ICON_ONLY_GLYPH_SIZE = {
    sm: 16,
    md: 20,
    lg: 22,
};
const BTN_VARIANTS = {
    // Disabled = pale `--accent` fill + muted text, matching secondary /
    // ghost / soft. The old `--muted-foreground` background (dark gray-brown)
    // read as an active dark button because the lightness was nearly the
    // same as `--primary` — users couldn't tell disabled apart from enabled.
    primary: ({ hover, active, disabled }) => ({
        background: disabled ? 'var(--accent)' : active ? 'var(--primary-active)' : hover ? '#6e1113' : 'var(--primary)',
        color: disabled ? 'var(--muted-foreground)' : 'var(--background)',
    }),
    secondary: ({ hover, active, disabled }) => ({
        background: disabled ? 'var(--accent)' : active ? 'var(--accent)' : hover ? 'var(--accent)' : 'var(--card)',
        color: disabled ? 'var(--muted-foreground)' : 'var(--foreground)',
        border: `1px solid ${hover && !disabled ? 'var(--border-strong)' : 'var(--border)'}`,
    }),
    ghost: ({ hover, active, disabled }) => ({
        background: disabled ? 'transparent' : active ? 'var(--accent)' : hover ? 'var(--background)' : 'transparent',
        color: disabled ? 'var(--muted-foreground)' : 'var(--foreground)',
    }),
    ghostDanger: ({ hover, active, disabled }) => ({
        background: disabled ? 'transparent' : active ? 'rgba(178,31,36,.12)' : hover ? 'rgba(178,31,36,.06)' : 'transparent',
        color: disabled ? 'var(--muted-foreground)' : 'var(--destructive)',
    }),
    soft: ({ hover, active, disabled }) => ({
        background: disabled ? 'var(--accent)' : active ? 'var(--border)' : hover ? '#ebe2dd' : 'var(--accent)',
        color: disabled ? 'var(--muted-foreground)' : 'var(--foreground)',
    }),
    // Status indicator: "you've already done this." Flat across all states —
    // no hover/active shift — so consumers can render it next to a primary CTA
    // without it competing for attention. Pair with `disabled` to mark the
    // control non-interactive; the cursor stays `default` rather than the
    // global `not-allowed`, since "Joined" reads as completed, not forbidden.
    success: () => ({
        background: 'var(--light-green)',
        color: 'var(--palette-emerald)',
        border: '1px solid color-mix(in oklab, var(--success) 25%, transparent)',
    }),
    // Sibling of `success` — same flat status-indicator shape, in the
    // burgundy tone palette. Used for "you're going" / "you've committed
    // to this" states where green would read as a successful completion
    // rather than an active commitment.
    softDanger: () => ({
        background: 'var(--light-poppy)',
        color: 'var(--primary)',
        border: '1px solid color-mix(in oklab, var(--primary) 22%, transparent)',
    }),
    onDark: ({ hover, active, disabled }) => ({
        background: disabled ? 'rgba(255,255,255,.4)' : active ? 'rgba(255,255,255,.85)' : hover ? 'rgba(255,255,255,.95)' : 'var(--background)',
        color: 'var(--foreground)',
    }),
    onDarkGhost: ({ hover, active, disabled }) => ({
        background: active ? 'rgba(255,255,255,.16)' : hover ? 'rgba(255,255,255,.10)' : 'transparent',
        color: disabled ? 'rgba(255,255,255,.4)' : 'var(--background)',
        border: '1px solid rgba(255 255 255 / 12%)',
    }),
    onDarkSuccess: ({ hover, active, disabled }) => ({
        background: disabled
            ? 'rgba(46,160,67,.4)'
            : active
                ? 'var(--emerald-deep, #2a8b3a)'
                : hover
                    ? 'var(--emerald-hover, #2f9b41)'
                    : 'var(--emerald, #36a14a)',
        color: 'var(--background)',
    }),
    link: ({ hover, disabled }) => ({
        background: 'transparent',
        // Inline text, not a box: no transparent border nudging the label off the
        // content edge it shares with the text around it.
        borderWidth: 0,
        color: disabled ? 'var(--muted-foreground)' : 'var(--primary)',
        height: 'auto',
        textDecoration: hover && !disabled ? 'underline' : 'none',
        textUnderlineOffset: 3,
    }),
    // A secondary text action ("View more comments") that recedes like the
    // muted meta text around it and darkens to the reading color on hover —
    // no underline, no brand color, so it never competes with the content.
    linkQuiet: ({ hover, disabled }) => ({
        background: 'transparent',
        borderWidth: 0,
        // Disabled fades below the resting muted tone, so it reads as inert.
        color: disabled
            ? 'var(--muted-foreground)'
            : hover
                ? 'var(--foreground)'
                : 'var(--muted-foreground)',
        opacity: disabled ? 0.5 : 1,
        height: 'auto',
    }),
};
export const Button = forwardRef(function Button(props, forwardedRef) {
    const objectRef = useObjectRef(forwardedRef);
    // Pull trigger props (onPress, aria-haspopup, aria-expanded, …) out of
    // ButtonContext when this Button is the slot for a RAC trigger like
    // MenuTrigger or DialogTrigger. Without this the trigger silently no-ops.
    const [merged, ref] = useContextProps(props, objectRef, ButtonContext);
    const resolved = merged;
    // An `href` makes this a link: it navigates, so assistive tech must hear
    // "link", not "button". Each element owns its react-aria hook — useButton
    // stamps role="button" on whatever it renders, useLink keeps the anchor's
    // native link role and activation.
    return resolved.href === undefined ? (_jsx(NativeButton, { ...resolved, elementRef: ref })) : (_jsx(AnchorButton, { ...resolved, href: resolved.href, elementRef: ref }));
});
function NativeButton({ elementRef, type = 'button', form, name, value, onPress: contextOnPress, ...props }) {
    const { visual, aria } = splitSurfaceProps(props);
    const noClick = !!visual.disabled || !!visual.loading;
    // RAC triggers (MenuTrigger, DialogTrigger) supply `onPress` via
    // ButtonContext — that path already activates on keyboard and stays
    // authoritative. Everything else activates through the DOM `onClick`
    // (see handleClick's stopPropagation rationale), but react-aria
    // suppresses the browser's native Enter/Space → click synthesis, so
    // without a bridge keyboard activation is silently dropped: pointer
    // clicks work while Enter/Space on a focused Button (or a Radix
    // Popover trigger cloned onto it) does nothing. Re-dispatch a real
    // click so keyboard and pointer take the identical code path.
    const { buttonProps, isPressed } = useButton({
        ...aria,
        isDisabled: noClick,
        elementType: 'button',
        type,
        onPress: (e) => {
            if (contextOnPress) {
                contextOnPress(e);
                return;
            }
            if (e.pointerType === 'keyboard' && e.target instanceof HTMLElement) {
                e.target.click();
            }
        },
    }, elementRef);
    const surface = useButtonSurface(visual, aria, isPressed, false);
    return (_jsx("button", { ...mergeProps(buttonProps, surface.interactionProps), ref: elementRef, form: form, name: name, value: value, slot: surface.domSlot, "aria-busy": surface.isBusy || undefined, className: surface.className, style: surface.style, children: surface.children }));
}
function AnchorButton({ elementRef, href, linkAs, target, rel, onPress: contextOnPress, type: _type, form: _form, name: _name, value: _value, ...props }) {
    const { visual, aria } = splitSurfaceProps(props);
    const noClick = !!visual.disabled || !!visual.loading;
    // `href` is deliberately not handed to useLink: without a react-aria
    // RouterProvider its handleLinkClick is a no-op, and navigation belongs to
    // the native anchor or `linkAs` (TanStack Link) alone. Keyboard needs no
    // bridge here — usePress leaves Enter on an anchor to the browser.
    const { linkProps, isPressed } = useLink({ ...aria, isDisabled: noClick, onPress: contextOnPress }, elementRef);
    const surface = useButtonSurface(visual, aria, isPressed, true);
    // Disabled or loading: no href, so the anchor is inert, and the plain `<a>`
    // rather than `linkAs` — router links require an href. An `<a>` without
    // href has no implicit role, so restore "link" for the aria-disabled pattern.
    const Anchor = noClick ? 'a' : (linkAs ?? 'a');
    return (_jsx(Anchor, { ...mergeProps(linkProps, surface.interactionProps), ref: elementRef, href: noClick ? undefined : href, role: noClick ? 'link' : undefined, target: target, rel: rel, slot: surface.domSlot, "aria-busy": surface.isBusy || undefined, "aria-disabled": noClick || undefined, className: surface.className, style: surface.style, children: surface.children }));
}
// Visual props drive the shared surface; everything else (aria-*, and the
// RAC trigger props ButtonContext supplies — onPressStart, aria-expanded, …)
// feeds the element's react-aria hook and lands on the DOM element.
function splitSurfaceProps({ variant, size, icon, iconSize, iconRight, padX, flush, underline, block, disabled, loading, onClick, children, style, className, slot, ...aria }) {
    return {
        visual: { variant, size, icon, iconSize, iconRight, padX, flush, underline, block, disabled, loading, onClick, children, style, className, slot },
        aria,
    };
}
function useButtonSurface(visual, aria, isPressed, asAnchor) {
    const { variant = 'primary', size = 'md', icon, iconSize, iconRight, padX, flush, underline, block, disabled, loading, onClick, children, style, className = '', slot, } = visual;
    const isBusy = !!loading;
    const noClick = !!disabled || isBusy;
    const { hoverProps, isHovered } = useHover({ isDisabled: noClick });
    const { focusProps, isFocusVisible } = useFocusRing();
    const forced = useContext(ForceStateContext);
    const state = {
        hover: !noClick && (forced?.hover ?? isHovered),
        active: !noClick && (forced?.active ?? isPressed),
        focus: !noClick && (forced?.focus ?? isFocusVisible),
        focusForced: !!forced?.focus && !noClick,
        // Loading buttons stay visually enabled — only true `disabled` greys out.
        disabled: !!disabled,
    };
    const variantStyle = BTN_VARIANTS[variant](state);
    // Icon-only mode: when there's an icon and no text/right-icon, render as a
    // square hit-target sized off the height tier so the button reads as a
    // glyph affordance rather than a stretched chip. A loading icon-only button
    // (spinner only) follows the same shape.
    const hasLeftSlot = icon !== undefined || isBusy;
    const iconOnly = hasLeftSlot && children === undefined && iconRight === undefined;
    const iconOnlyStyle = iconOnly
        ? { width: BTN_SIZES[size].height, justifyContent: 'center', gap: 0 }
        : undefined;
    // The one source of the inline padding and border, so `flush` always undoes
    // exactly what was applied: no padding for icon-only squares or text links,
    // and no border for text links.
    const textLink = TEXT_LINK_VARIANTS.has(variant);
    const padInline = iconOnly || textLink ? '0px' : `var(--space-${padX ?? BTN_PAD_X[size]})`;
    const borderInline = textLink ? '0px' : BTN_BORDER_WIDTH;
    const flushStyle = flush === undefined
        ? undefined
        : {
            [flush === 'start' ? 'marginInlineStart' : 'marginInlineEnd']: `calc(-1 * (${padInline} + ${borderInline}))`,
        };
    const iconGlyphSize = iconSize ?? (iconOnly ? ICON_ONLY_GLYPH_SIZE[size] : 16);
    // Buttons are routinely rendered inside a clickable parent (Link, Card,
    // ListItem). Without stopPropagation the click runs the button's onClick
    // and *also* fires the parent's navigation — a bug shape we have hit
    // repeatedly. When the caller has no onClick the button is being used as
    // a styled child of an outer link (e.g. <Link><Button>Open</Button></Link>),
    // so we leave bubbling alone.
    const handleClick = onClick
        ? (e) => {
            e.stopPropagation();
            if (noClick)
                return;
            // onClick is typed for the button branch; in anchor mode the same
            // handler is rarely useful (consumers navigate via href) and the
            // event shape difference is structural only.
            onClick(e);
        }
        : undefined;
    const surfaceStyle = {
        ...BTN_BASE,
        ...BTN_SIZES[size],
        padding: `0 ${padInline}`,
        ...flushStyle,
        ...variantStyle,
        // `success` is a status indicator ("Joined") — when paired with
        // `disabled` it should read as completed, not as a rejected control,
        // so its cursor stays `default` rather than the global `not-allowed`.
        cursor: isBusy ? 'wait' : disabled ? (variant === 'success' ? 'default' : 'not-allowed') : 'pointer',
        ...focusRing(state.focus),
        ...(block ? { width: '100%', justifyContent: 'center' } : null),
        ...iconOnlyStyle,
        // Anchors inherit text-decoration from the page — reset so the button
        // surface never shows the default underline. Belt-and-suspenders with
        // BTN_BASE letterSpacing.
        ...(asAnchor ? { textDecoration: 'none' } : null),
        ...(underline ? { textDecoration: 'underline', textUnderlineOffset: 3 } : null),
        ...style,
    };
    return {
        isBusy,
        // `slot: null` is meaningful for RAC ButtonContext opt-out but not
        // assignable to the DOM `slot` attribute (string | undefined).
        domSlot: slot ?? undefined,
        className: className ? `${styles.surface} ${className}` : styles.surface,
        style: surfaceStyle,
        interactionProps: mergeProps(hoverProps, focusProps, aria, { onClick: handleClick }),
        children: (_jsxs(_Fragment, { children: [isBusy ? (_jsx(LeadingSlot, { labelled: children !== undefined, children: _jsx(Spinner, { label: "", style: { width: iconGlyphSize, height: iconGlyphSize } }) })) : icon ? (_jsx(LeadingSlot, { labelled: children !== undefined, children: _jsx(Icon, { size: iconGlyphSize, children: icon }) })) : null, children !== undefined && _jsx(TextTrim, { children: children }), iconRight && _jsx(Icon, { size: 16, children: iconRight })] })),
    };
}
// See .leadingSlot: gives a labelled button the label's baseline. Icon-only
// buttons have no label to align, so they render the visual bare.
function LeadingSlot({ labelled, children }) {
    if (!labelled)
        return _jsx(_Fragment, { children: children });
    return _jsx("span", { className: styles.leadingSlot, children: children });
}
//# sourceMappingURL=Button.js.map